/**
 * discount-filter-live.js
 *
 * Problem: the Discount filter (filter.p.m.custom.discount_percentage) is
 * answered by Shopify's own filter index, and that index lags badly behind
 * bulk updates to custom.discount_percentage (seen 2026-09-30 / 10-01 after
 * the sync script rewrote ~4,500 values: "30%" returned products now at 72%,
 * and new values like 24 or 72 returned nothing). The metafield itself, the
 * badges and the facet worker's catalog were all correct - only Shopify's
 * index was stale.
 *
 * Fix: whenever Discount values are part of a filter request, decide WHICH
 * products match from the facet worker's live catalog instead:
 *   1. The request still goes to Shopify unchanged, purely for the facets
 *      markup (checkboxes ticked, active pills, sort) - its grid is ignored.
 *   2. The same request WITHOUT the discount param is fetched page by page
 *      (Shopify still applies every other filter - Size, Colour etc.).
 *   3. Only cards whose handle the worker says has a matching
 *      discount_percentage are kept, on one unpaginated grid.
 *
 * Hooks into facets.js by wrapping FacetFiltersForm.renderSectionFromFetch,
 * so sidebar ticks, active-pill removal, back/forward, the min/max_discount
 * shortcut links (layout/theme.liquid) and direct Discount URLs all go
 * through the same path. Anything unexpected (worker down, old worker
 * response without handles, fetch error) falls back to the original
 * Shopify-only behaviour.
 *
 * Checkbox list: Shopify only renders checkboxes for values its (lagging)
 * index knows about, so values that only exist in live data (e.g. 26 or 28
 * before the index catches up) had no checkbox. addMissingCheckboxes()
 * clones an existing Discount checkbox for every live value in this
 * collection that's missing, in numeric order, after every facets render.
 * facet-preview-counts.js then corrects their counts like any other box.
 */
(function () {
  if (typeof FacetFiltersForm === 'undefined') {
    // facets.js missing: at least turn min/max_discount links into a plain
    // (Shopify-filtered) Discount URL rather than ignoring them
    var p0 = new URLSearchParams(location.search);
    var lo = parseFloat(p0.get('min_discount'));
    var hi = parseFloat(p0.get('max_discount'));
    if (!isNaN(lo) || !isNaN(hi)) {
      p0.delete('min_discount');
      p0.delete('max_discount');
      for (var i = 1; i <= 100; i++) {
        if ((isNaN(lo) || i >= lo) && (isNaN(hi) || i <= hi)) p0.append('filter.p.m.custom.discount_percentage', String(i));
      }
      location.replace(location.pathname + '?' + p0.toString());
    }
    return;
  }

  var KEY = 'filter.p.m.custom.discount_percentage';
  var PMF_KEY = 'custom.discount_percentage';
  var WORKER_BASE_URL = /^us\./.test(location.hostname)
    ? 'https://lucyandyak-facet-catalog-us.lucy-and-yak.workers.dev'
    : 'https://lucyandyak-facet-catalog.lucy-and-yak.workers.dev';
  var CATALOG_TTL_MS = 5 * 60 * 1000; // matches the worker's refresh cadence
  var MAX_PAGES = 30;

  function collectionHandle() {
    var m = location.pathname.match(/\/collections\/([^\/\?]+)/);
    return m ? m[1] : null;
  }

  var catalogPromise = null;
  var catalogFetchedAt = 0;
  function fetchCatalog() {
    if (catalogPromise && Date.now() - catalogFetchedAt < CATALOG_TTL_MS) return catalogPromise;
    catalogFetchedAt = Date.now();
    // layout/theme.liquid starts this request from <head> on Discount URLs,
    // so it's usually already back by the time this script runs. One use only.
    var prefetched = window.__discountCatalogPrefetch;
    window.__discountCatalogPrefetch = null;
    catalogPromise = (prefetched || fetch(WORKER_BASE_URL + '?collection=' + encodeURIComponent(collectionHandle())))
      .then(function (r) {
        if (!r.ok) throw new Error('worker HTTP ' + r.status);
        return r.json();
      })
      .then(function (list) {
        if (!Array.isArray(list) || !list.length || !list[0].handle) {
          throw new Error('worker response has no handles');
        }
        return list;
      })
      .catch(function (err) {
        catalogPromise = null;
        throw err;
      });
    return catalogPromise;
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function fetchText(url) {
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
      return r.text();
    });
  }

  function gridCards(doc) {
    var grid = doc.getElementById('product-grid');
    if (!grid) return [];
    return Array.from(grid.children).filter(function (el) {
      return el.tagName === 'LI' && el.dataset.productHandle;
    });
  }

  function maxPage(doc) {
    var max = 1;
    doc.querySelectorAll('.pagination a[href*="page="]').forEach(function (a) {
      var m = a.getAttribute('href').match(/[?&]page=(\d+)/);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return Math.min(max, MAX_PAGES);
  }

  // Every card (other filters applied, discount removed), across all pages
  async function fetchAllCards(baseUrl) {
    var first = parse(await fetchText(baseUrl.toString()));
    var cards = gridCards(first);
    var last = maxPage(first);
    if (last > 1) {
      var urls = [];
      for (var page = 2; page <= last; page++) {
        var u = new URL(baseUrl.toString());
        u.searchParams.set('page', String(page));
        urls.push(u.toString());
      }
      var docs = await Promise.all(urls.map(function (u) { return fetchText(u).then(parse); }));
      docs.forEach(function (d) { cards = cards.concat(gridCards(d)); });
    }
    return { cards: cards, firstDoc: first };
  }

  function setLeadingCount(el, count) {
    el.innerHTML = el.innerHTML.replace(/^(\s*)[\d,]+/, '$1' + count);
  }

  async function buildLiveHtml(url) {
    var u = new URL(url, location.origin);
    var values = u.searchParams.getAll(KEY).map(function (v) { return String(parseInt(v, 10)); });

    var base = new URL(u.toString());
    base.searchParams.delete(KEY);
    base.searchParams.delete('page');

    var results = await Promise.all([fetchCatalog(), fetchText(u.toString()), fetchAllCards(base)]);
    var catalog = results[0];
    var doc = parse(results[1]);
    var cards = results[2].cards;

    var wanted = new Set();
    catalog.forEach(function (p) {
      var v = p.pmf && p.pmf[PMF_KEY];
      if (v != null && values.indexOf(String(parseInt(v, 10))) !== -1) wanted.add(p.handle);
    });

    var seen = new Set();
    var keep = cards.filter(function (li) {
      var h = li.dataset.productHandle;
      if (!wanted.has(h) || seen.has(h)) return false;
      seen.add(h);
      return true;
    });

    var grid = doc.getElementById('product-grid');
    // Shopify's stale results can be EMPTY, in which case its section renders
    // the empty-state <div id="product-grid"> instead of the <ul> card grid.
    // Borrow the real grid markup from the discount-free response instead.
    if (keep.length && (!grid || grid.tagName !== 'UL')) {
      var target = doc.getElementById('ProductGridContainer');
      var source = results[2].firstDoc.getElementById('ProductGridContainer');
      if (!target || !source) throw new Error('no #ProductGridContainer in section response');
      target.innerHTML = source.innerHTML;
      grid = doc.getElementById('product-grid');
    }
    if (!grid) throw new Error('no #product-grid in section response');
    grid.innerHTML = keep.map(function (li) { return li.outerHTML; }).join('');
    // One unpaginated page: stop grid-backfill.js treating it as a paged grid
    grid.removeAttribute('data-products-per-page');
    doc.querySelectorAll('#ProductGridContainer .pagination-wrapper').forEach(function (el) { el.remove(); });
    if (!keep.length) {
      grid.insertAdjacentHTML(
        'afterend',
        '<p class="title center discount-filter-live__empty">No products match these discounts right now.</p>'
      );
    }
    doc.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach(function (el) {
      setLeadingCount(el, keep.length);
    });

    return doc.documentElement.outerHTML;
  }

  function reveal() {
    document.documentElement.classList.remove('discount-range-loading');
  }

  function renderHtml(html, event) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  // Adds a checkbox for every live discount value in this collection that
  // Shopify's checkbox list is missing (desktop and mobile lists alike).
  function addMissingCheckboxes() {
    if (!collectionHandle()) return;
    if (!document.querySelector('input[name="' + KEY + '"]')) return; // no Discount filter here
    fetchCatalog()
      .then(function (catalog) {
        var liveCounts = {};
        catalog.forEach(function (p) {
          var v = p.pmf && parseInt(p.pmf[PMF_KEY], 10);
          if (v > 0) liveCounts[v] = (liveCounts[v] || 0) + 1;
        });
        var active = new Set(
          new URLSearchParams(location.search).getAll(KEY).map(function (v) { return parseInt(v, 10); })
        );

        var lists = new Set();
        document.querySelectorAll('input[name="' + KEY + '"]').forEach(function (input) {
          var li = input.closest('li');
          if (li && li.parentElement) lists.add(li.parentElement);
        });

        lists.forEach(function (list) {
          var items = function () {
            return Array.from(list.children).filter(function (li) {
              return li.querySelector('input[name="' + KEY + '"]');
            });
          };
          var template = items()[0];
          if (!template) return;
          var existing = new Set(items().map(function (li) {
            return parseInt(li.querySelector('input[name="' + KEY + '"]').value, 10);
          }));

          Object.keys(liveCounts).map(Number).sort(function (a, b) { return a - b; }).forEach(function (v) {
            if (existing.has(v)) return;
            var clone = template.cloneNode(true);
            clone.classList.remove('facet-zero-count-hidden', 'hidden', 'show-more-item');
            clone.removeAttribute('hidden');
            clone.dataset.discountLive = 'true';

            var input = clone.querySelector('input[name="' + KEY + '"]');
            var id = input.id + '-live-' + v;
            input.value = String(v);
            input.id = id;
            input.checked = active.has(v);
            input.disabled = false;
            input.removeAttribute('disabled');

            var label = clone.querySelector('label');
            if (label) {
              label.htmlFor = id;
              label.classList.remove('disabled');
              label.classList.toggle('active', active.has(v));
            }
            var text = clone.querySelector('.facet-checkbox__text');
            if (text) text.innerHTML = text.innerHTML.replace(/\(\s*[\d,]+\s*\)\s*$/, '(' + liveCounts[v] + ')');
            var textLabel = clone.querySelector('.facet-checkbox__text-label');
            if (textLabel) textLabel.textContent = String(v);
            var hidden = clone.querySelector('.visually-hidden');
            if (hidden) hidden.textContent = v + ' (' + liveCounts[v] + ')';

            // numeric position among the existing Discount items
            var after = null;
            items().forEach(function (li) {
              if (parseInt(li.querySelector('input[name="' + KEY + '"]').value, 10) < v) after = li;
            });
            if (after) after.after(clone);
            else template.before(clone);
            existing.add(v);
          });
        });
      })
      .catch(function () { /* worker unavailable - leave Shopify's list as is */ });
  }

  // ---- Active filter pills ----
  //
  // Shopify only renders a pill for Discount values its (lagging) index
  // knows, and none at all on a min/max_discount landing (the served page
  // wasn't Discount-filtered). So Shopify's own Discount pills are replaced
  // with ONE summary pill per pills bar, e.g. "Discount: 25-28%, 30%" or
  // "Discount: 50-100%", whose x clears every Discount value at once.
  var CLOSE_ICON =
    '<svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function rangesLabel(values) {
    var sorted = Array.from(new Set(values)).sort(function (a, b) { return a - b; });
    var parts = [];
    for (var i = 0; i < sorted.length; i++) {
      var start = sorted[i];
      while (i + 1 < sorted.length && sorted[i + 1] === sorted[i] + 1) i++;
      parts.push(start === sorted[i] ? start + '%' : start + '–' + sorted[i] + '%');
    }
    return parts.join(', ');
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var discountFilterLabel = 'Discount';
  function renderDiscountPills() {
    var values = new URLSearchParams(location.search).getAll(KEY)
      .map(function (v) { return parseInt(v, 10); })
      .filter(function (v) { return v > 0; });

    var removeUrl = new URL(location.href);
    removeUrl.searchParams.delete(KEY);
    removeUrl.searchParams.delete('page');

    document.querySelectorAll('.active-facets').forEach(function (bar) {
      bar.querySelectorAll('facet-remove').forEach(function (pill) {
        if (pill.dataset.discountLive) return pill.remove();
        var inner = pill.querySelector('.active-facets__button-inner');
        if (!inner) return;
        // Shopify's own Discount pills: "<label>: <value>" whose link drops a Discount value
        var link = pill.querySelector('a[href]');
        var href = link ? new URL(link.getAttribute('href'), location.origin) : null;
        var text = inner.textContent.trim();
        var m = text.match(/^([^:]+):\s*\d+\s*$/);
        // (its "remove" link has exactly one Discount value fewer than the page)
        if (m && href && values.length && href.searchParams.getAll(KEY).length === values.length - 1) {
          discountFilterLabel = m[1].trim();
          pill.remove();
        }
      });
      if (!values.length) return;

      var html =
        '<facet-remove data-discount-live="true">' +
        '<a href="' + escapeHtml(removeUrl.pathname + removeUrl.search) + '" class="active-facets__button active-facets__button--light">' +
        '<span class="active-facets__button-inner button button--tertiary">' +
        escapeHtml(discountFilterLabel) + ': ' + escapeHtml(rangesLabel(values)) + ' ' + CLOSE_ICON +
        '<span class="visually-hidden">Remove filter</span>' +
        '</span></a></facet-remove>';
      var clearAll = bar.querySelector('facet-remove.active-facets__button-wrapper');
      if (clearAll) clearAll.insertAdjacentHTML('beforebegin', html);
      else bar.insertAdjacentHTML('beforeend', html);
    });
  }

  var originalRenderFilters = FacetFiltersForm.renderFilters;
  FacetFiltersForm.renderFilters = function (html, event) {
    originalRenderFilters.call(FacetFiltersForm, html, event);
    addMissingCheckboxes();
    renderDiscountPills();
  };

  var originalRenderSectionFromFetch = FacetFiltersForm.renderSectionFromFetch;
  FacetFiltersForm.renderSectionFromFetch = function (url, event) {
    var hasDiscount = new URL(url, location.origin).searchParams.getAll(KEY).length > 0;
    if (!hasDiscount || !collectionHandle()) {
      return originalRenderSectionFromFetch.call(FacetFiltersForm, url, event);
    }
    buildLiveHtml(url)
      .then(function (html) {
        // Cache under the same url facets.js uses, so renderSectionFromCache
        // (repeat clicks, back/forward) gets the corrected grid too
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: html, url: url }];
        renderHtml(html, event);
        reveal();
      })
      .catch(function (err) {
        console.warn('[discount-filter-live] falling back to Shopify results:', err);
        originalRenderSectionFromFetch.call(FacetFiltersForm, url, event);
        reveal();
      });
  };

  // Direct page loads of a Discount URL (nav links, shared links, refresh):
  // the server-rendered grid came from Shopify's stale index, so re-render it.
  // min_discount/max_discount URLs are left to layout/theme.liquid, which
  // ends up calling renderPage (and so the wrapper above) itself.
  // ---- Direct page loads (nav bucket links, shared links, refresh) ----
  //
  // The server-rendered grid on a Discount URL came from Shopify's stale
  // index, so it's replaced - but without going back through facets.js:
  //   - no re-fetch of the Discount-filtered section (the facets already on
  //     the page are right; only the grid is wrong), and
  //   - every page of the discount-free listing is fetched IN PARALLEL, the
  //     page count estimated up front from the worker catalog, rather than
  //     page 1 first and the rest after.
  // For min_discount/max_discount links the page as served IS page 1 of the
  // discount-free listing (Shopify ignores those params), so that page is
  // reused and only pages 2+ are fetched.
  async function liveInitialLoad(params, values, shortcut) {
    var grid = document.getElementById('product-grid');
    var container = document.getElementById('ProductGridContainer');
    if (!grid || !container) return;

    var sectionId = grid.dataset.id;
    var perPage = parseInt(grid.dataset.discountPerPage || grid.dataset.productsPerPage, 10) || 72;

    var base = new URL(location.pathname, location.origin);
    params.forEach(function (v, k) {
      if (k !== KEY && k !== 'page' && k !== 'min_discount' && k !== 'max_discount') base.searchParams.append(k, v);
    });
    base.searchParams.set('section_id', sectionId);

    // Served page is reusable as page 1 only if nothing else was filtering it
    var otherFilters = Array.from(base.searchParams.keys()).some(function (k) { return /^filter\./.test(k); });
    var reuseServedPage = shortcut && !otherFilters && !params.has('page') && grid.tagName === 'UL';
    var servedCards = reuseServedPage ? gridCards(document) : [];

    var catalog = await fetchCatalog();
    var pageCount = Math.max(1, Math.ceil(catalog.length / perPage));
    var pageUrl = function (n) {
      var u = new URL(base.toString());
      u.searchParams.set('page', String(n));
      return u.toString();
    };

    var wanted = new Set();
    catalog.forEach(function (p) {
      var v = p.pmf && parseInt(p.pmf[PMF_KEY], 10);
      if (v > 0 && values.indexOf(v) !== -1) wanted.add(p.handle);
    });
    var seen = new Set();
    var shown = 0;
    var pick = function (cards) {
      return cards.filter(function (li) {
        var h = li.dataset.productHandle;
        if (!wanted.has(h) || seen.has(h)) return false;
        seen.add(h);
        return true;
      });
    };

    // All pages requested at once, but shown progressively IN PAGE ORDER as
    // each arrives - so the first matches appear as soon as possible (for
    // min/max links, straight away from the page already served).
    var firstPage = reuseServedPage ? 2 : 1;
    var pending = [];
    for (var n = firstPage; n <= pageCount; n++) {
      pending.push(fetchText(pageUrl(n)).then(parse));
    }

    var prepared = false;
    var prepare = function (sourceDoc) {
      // Shopify's stale results may have been empty (empty-state <div>), so
      // borrow the real grid markup from a discount-free page if needed
      if (grid.tagName !== 'UL' && sourceDoc) {
        var source = sourceDoc.getElementById('ProductGridContainer');
        if (source) container.innerHTML = source.innerHTML;
        grid = document.getElementById('product-grid');
      }
      grid.innerHTML = '';
      grid.removeAttribute('data-products-per-page');
      container.querySelectorAll('.pagination-wrapper, .discount-filter-live__empty, .discount-filter-live__more')
        .forEach(function (el) { el.remove(); });
      if (pending.length) {
        grid.insertAdjacentHTML('afterend', '<div class="discount-filter-live__more" aria-hidden="true"></div>');
      }
      prepared = true;
    };
    var append = function (cards) {
      if (!cards.length) return;
      grid.insertAdjacentHTML('beforeend', cards.map(function (li) { return li.outerHTML; }).join(''));
      shown += cards.length;
      document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach(function (el) {
        setLeadingCount(el, shown);
      });
      reveal();
    };

    if (reuseServedPage) {
      var servedMatches = pick(servedCards);
      prepare(null);
      append(servedMatches);
    }

    var lastDoc = null;
    for (var k = 0; k < pending.length; k++) {
      var d = await pending[k];
      lastDoc = d;
      if (!prepared) prepare(d);
      append(pick(gridCards(d)));
    }
    if (!prepared) prepare(null);

    // Page-count estimate too low (shouldn't happen, but cheap to cover)
    var lastSeen = lastDoc ? maxPage(lastDoc) : 1;
    for (var m = pageCount + 1; m <= lastSeen; m++) {
      append(pick(gridCards(parse(await fetchText(pageUrl(m))))));
    }

    container.querySelectorAll('.discount-filter-live__more').forEach(function (el) { el.remove(); });
    document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach(function (el) {
      setLeadingCount(el, shown);
    });
    if (!shown) {
      grid.insertAdjacentHTML(
        'afterend',
        '<p class="title center discount-filter-live__empty">No products match these discounts right now.</p>'
      );
    }

    if (shortcut) {
      // Tick the matching boxes the server didn't know were active
      document.querySelectorAll('input[name="' + KEY + '"]').forEach(function (input) {
        input.checked = values.indexOf(parseInt(input.value, 10)) !== -1;
      });
    }
    addMissingCheckboxes();
    renderDiscountPills();
  }

  (function startInitialLoad() {
    if (!collectionHandle()) return;
    var params = new URLSearchParams(location.search);
    var min = parseFloat(params.get('min_discount'));
    var max = parseFloat(params.get('max_discount'));
    var shortcut = !isNaN(min) || !isNaN(max);
    var values;

    if (shortcut) {
      // Every whole percentage in range - not just the values in Shopify's
      // lagging checkbox list
      values = [];
      for (var i = 1; i <= 100; i++) {
        if ((isNaN(min) || i >= min) && (isNaN(max) || i <= max)) values.push(i);
      }
      var full = new URLSearchParams();
      params.forEach(function (v, k) {
        if (k !== 'min_discount' && k !== 'max_discount' && k !== KEY) full.append(k, v);
      });
      values.forEach(function (v) { full.append(KEY, String(v)); });
      var qs = full.toString();
      history.replaceState(history.state, '', location.pathname + '?' + qs);
      FacetFiltersForm.searchParamsInitial = qs;
      FacetFiltersForm.searchParamsPrev = qs;
    } else {
      values = params.getAll(KEY).map(function (v) { return parseInt(v, 10); });
    }

    if (!values.length) {
      addMissingCheckboxes();
      return reveal();
    }

    // Stop grid-backfill.js (loaded after this file) from paging the stale
    // grid while the live one is being built
    var grid = document.getElementById('product-grid');
    if (grid && grid.dataset.productsPerPage) {
      grid.dataset.discountPerPage = grid.dataset.productsPerPage; // still needed for the page count
      grid.removeAttribute('data-products-per-page');
    }

    setTimeout(reveal, 10000); // failsafe: never leave the grid hidden
    liveInitialLoad(params, values, shortcut)
      .catch(function (err) {
        console.warn('[discount-filter-live] initial load fell back to Shopify results:', err);
      })
      .then(reveal);
  })();
})();
